import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, CheckCircle2, AlertCircle } from 'lucide-react';
import { formatDate } from '../../utils/formatDate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SiteReview() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | submitted | already | error
  const [context, setContext] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/reviews/token/${token}`);
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          setErrorMsg(err.detail || 'Lien invalide');
          setState('error');
          return;
        }
        const data = await r.json();
        setContext(data);
        if (data.already_submitted) {
          setRating(data.rating || 0);
          setComment(data.comment || '');
          setState('already');
        } else {
          setState('ready');
        }
      } catch (e) {
        setErrorMsg('Erreur réseau');
        setState('error');
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/reviews/token/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comment }),
      });
      if (r.ok) {
        setState('submitted');
      } else {
        const err = await r.json().catch(() => ({}));
        setErrorMsg(err.detail || 'Erreur lors de l’envoi');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="site-section" data-testid="review-page" style={{ maxWidth: 640, margin: '0 auto' }}>
      <div className="site-card" style={{ padding: '2rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ marginBottom: '.5rem' }}>Votre avis compte pour nous</h1>
          <p style={{ color: '#64748b' }}>
            Centre Médical La Trinité — Matadi
          </p>
        </div>

        {state === 'loading' && <p style={{ textAlign: 'center' }}>Chargement…</p>}

        {state === 'error' && (
          <div style={{ textAlign: 'center', color: '#dc2626' }} data-testid="review-error">
            <AlertCircle size={32} />
            <p>{errorMsg}</p>
            <Link to="/" className="site-btn site-btn-primary" style={{ marginTop: '1rem' }}>Retour au site</Link>
          </div>
        )}

        {state === 'already' && (
          <div style={{ textAlign: 'center' }} data-testid="review-already">
            <CheckCircle2 size={40} color="#16a34a" />
            <p>Vous avez déjà soumis votre avis. Merci !</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 4, margin: '1rem 0' }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} size={28} fill={i <= (rating || 0) ? '#f59e0b' : 'none'} stroke="#f59e0b" />
              ))}
            </div>
            {comment && <p style={{ fontStyle: 'italic', color: '#475569' }}>« {comment} »</p>}
            <Link to="/" className="site-btn site-btn-primary" style={{ marginTop: '1rem' }}>Retour au site</Link>
          </div>
        )}

        {state === 'submitted' && (
          <div style={{ textAlign: 'center' }} data-testid="review-submitted">
            <CheckCircle2 size={48} color="#16a34a" />
            <h2 style={{ marginTop: '.5rem' }}>Merci pour votre avis !</h2>
            <p style={{ color: '#475569' }}>Votre retour nous aide à améliorer nos soins.</p>
            <Link to="/" className="site-btn site-btn-primary" style={{ marginTop: '1rem' }}>Retour au site</Link>
          </div>
        )}

        {state === 'ready' && context && (
          <form onSubmit={submit} data-testid="review-form">
            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: 8, marginBottom: '1.5rem' }}>
              {context.patient_first_name && (
                <p style={{ margin: 0 }}>
                  Bonjour <strong>{context.patient_first_name}</strong>,
                </p>
              )}
              <p style={{ margin: '.25rem 0 0' }}>
                Comment évaluez-vous votre consultation
                {context.doctor_name && (
                  <> avec le <strong>Dr {context.doctor_name}</strong>{context.doctor_specialty ? ` (${context.doctor_specialty})` : ''}</>
                )}
                {context.appointment_date && <> du <strong>{formatDate(context.appointment_date)}</strong></>}
                {' '}?
              </p>
            </div>

            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ display: 'inline-flex', gap: 8 }} role="radiogroup" aria-label="Note">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button
                    type="button"
                    key={i}
                    onMouseEnter={() => setHover(i)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(i)}
                    aria-label={`${i} étoile${i > 1 ? 's' : ''}`}
                    data-testid={`star-${i}`}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 4,
                    }}
                  >
                    <Star
                      size={40}
                      fill={i <= (hover || rating) ? '#f59e0b' : 'none'}
                      stroke="#f59e0b"
                      strokeWidth={2}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p style={{ marginTop: '.5rem', color: '#475569' }}>
                  {['Très insatisfait', 'Insatisfait', 'Correct', 'Satisfait', 'Excellent'][rating - 1]}
                </p>
              )}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                Un commentaire (optionnel)
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={1000}
                rows={4}
                placeholder="Partagez votre expérience…"
                data-testid="review-comment"
                style={{
                  width: '100%',
                  padding: '.75rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: 8,
                  fontFamily: 'inherit',
                  fontSize: '1rem',
                  resize: 'vertical',
                }}
              />
              <small style={{ color: '#94a3b8' }}>{comment.length}/1000</small>
            </div>

            {errorMsg && (
              <p style={{ color: '#dc2626', marginBottom: '1rem' }}>{errorMsg}</p>
            )}

            <button
              type="submit"
              className="site-btn site-btn-primary"
              disabled={!rating || submitting}
              data-testid="review-submit"
              style={{ width: '100%', padding: '.85rem', fontSize: '1rem' }}
            >
              {submitting ? 'Envoi…' : 'Envoyer mon avis'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
